import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/use-auth-store';
import { subscriptionService, type SubscriptionPlan } from '@/services/subscription/subscription.service';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface OnboardingPageProps {
  onComplete?: (planId?: string) => void;
  companyId?: string;
}

const OnboardingPage = ({ onComplete, companyId }: OnboardingPageProps = {}) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const planCode = searchParams.get('planCode');
  
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);

  useEffect(() => {
    loadPlans();
  }, [isAuthenticated, planCode]);


  const loadPlans = async () => {
    setIsLoadingPlans(true);
    try {
      const response = await subscriptionService.getPlans();
      const plansList = Array.isArray(response) ? response : (response.data || []);
      setPlans(plansList);
      
      if (planCode && plansList.length > 0) {
        const plan = plansList.find(
          (p: SubscriptionPlan) => p.paymentProcessorId === planCode
        );
        if (plan) {
          setSelectedPlan(plan);
        }
      }
    } catch (error) {
      console.error('Failed to load plans:', error);
      toast.error(t('onboarding.toasts.failedToLoadPlans'));
    } finally {
      setIsLoadingPlans(false);
    }
  };

  const handleCreateSubscription = async () => {
    if (!selectedPlan) {
      toast.error(t('onboarding.toasts.pleaseSelectPlan'));
      return;
    }

    if (!isAuthenticated) {
      toast.error(t('onboarding.toasts.pleaseSignIn'));
      return;
    }

    if (onComplete) {
      // If used within multi-step flow (onboarding or create-company), 
      // call onComplete with planId and let parent handle navigation
      onComplete(selectedPlan.id);
    } else {
      // Standalone usage - navigate directly to checkout
      const checkoutUrl = `/onboarding/checkout?planId=${selectedPlan.id}`;
      navigate(checkoutUrl);
    }
  };

  if (!isAuthenticated) {
    const redirectUrl = planCode ? `/onboarding?planCode=${planCode}` : '/onboarding';
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="max-w-md w-full space-y-6 text-center">
          <h1 className="text-2xl font-bold">{t('onboarding.completeSubscription')}</h1>
          <p className="text-gray-600">{t('onboarding.signInToContinue')}</p>
          <div className="space-y-3">
            <Link to={`/auth/login?redirect=${encodeURIComponent(redirectUrl)}`}>
              <Button className="w-full">{t('onboarding.signIn')}</Button>
            </Link>
            <Link to={`/auth/register?redirect=${encodeURIComponent(redirectUrl)}`}>
              <Button variant="outline" className="w-full">{t('onboarding.createAccount')}</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }


  const visiblePlans = plans.filter(
    (plan) => plan.paymentProcessorId && plan.priceInCents > 0
  );

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-[6vw] py-8 sm:py-12 flex flex-col gap-8 sm:gap-10 max-w-[85rem]">
      <div className="w-full sm:w-2/3 mx-auto flex flex-col gap-4 justify-center">
        <h1 className="text-2xl sm:text-3xl font-bold text-center">{t('onboarding.title')}</h1>
        <p className="text-sm sm:text-base text-gray-600 text-center">
          {t('onboarding.subtitle')}
        </p>
      </div>

      {isLoadingPlans ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('onboarding.loadingPlans')}</p>
        </div>
      ) : visiblePlans.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-600">{t('onboarding.noPlansAvailable')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 sm:gap-6 justify-items-center">
          {visiblePlans.map((plan) => {
            const features = plan.features as {
              storage?: number;
              ram?: number;
              unlimitedUsers?: boolean;
              maxStaffUsers?: number;
            } | undefined;

            return (
              <div
                key={plan.id}
                className={`rounded-lg border md:p-6 p-4 w-full max-w-sm lg:max-w-md xl:max-w-lg cursor-pointer transition-all ${
                  selectedPlan?.id === plan.id
                    ? 'border-blue-500 ring-2 ring-blue-200'
                    : 'border-brand/5 hover:border-gray-300'
                }`}
                onClick={() => setSelectedPlan(plan)}
              >
                <h2 className="text-xl font-semibold">{plan.displayName}</h2>
                <p className="text-gray-600 mt-1">{plan.description}</p>
                <hr className="my-4 opacity-30" />
                <div className="mb-4 text-gray-500 flex gap-2 items-end">
                  <span className="text-2xl font-bold">
                    ${(plan.priceInCents).toLocaleString('en-US')}
                  </span>
                  <span>/</span>
                  <span>{t('onboarding.month')}</span>
                </div>
                <ul className="space-y-2 mb-6">
                  {(features?.unlimitedUsers !== false) && (
                    <li className="flex items-center space-x-2">
                      <CheckCircleIcon className="w-5 h-5 text-blue-500" />
                      <span className="text-gray-700">{t('onboarding.unlimitedUsers')}</span>
                    </li>
                  )}
                  {features?.ram && (
                    <li className="flex items-center space-x-2">
                      <CheckCircleIcon className="w-5 h-5 text-blue-500" />
                      <span className="text-gray-700">
                        {features.ram}{t('onboarding.gbRam')}
                      </span>
                    </li>
                  )}
                  {features?.storage && (
                    <li className="flex items-center space-x-2">
                      <CheckCircleIcon className="w-5 h-5 text-blue-500" />
                      <span className="text-gray-700">
                        {features.storage}{t('onboarding.gbSsdStorage')}
                      </span>
                    </li>
                  )}
                  {features?.maxStaffUsers !== undefined && (
                    <li className="flex items-center space-x-2">
                      <CheckCircleIcon className="w-5 h-5 text-blue-500" />
                      <span className="text-gray-700">
                        {features.maxStaffUsers === -1 
                          ? t('onboarding.unlimitedStaffMembers')
                          : t('onboarding.upToStaffMembers', { count: features.maxStaffUsers })
                        }
                      </span>
                    </li>
                  )}
                </ul>
                {selectedPlan?.id === plan.id && (
                  <p className="text-blue-500 font-medium mb-2">{t('onboarding.selected')}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {plans.length > 0 && (
        <div className="mt-8 space-y-4 w-full max-w-2xl lg:max-w-3xl xl:max-w-4xl mx-auto">
          <div className="bg-gray-50 p-4 sm:p-6 rounded-lg">
            <h2 className="text-lg sm:text-xl font-semibold mb-4">{t('onboarding.selectedPlan')}</h2>
            <div className="space-y-2 text-sm sm:text-base">
              {selectedPlan ? (
                <>
                  <p><strong>{t('onboarding.plan')}</strong> {selectedPlan.displayName}</p>
                  <p><strong>{t('onboarding.price')}</strong> ${(selectedPlan.priceInCents).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / {t('onboarding.month')}</p>
                </>
              ) : (
                <p className="text-gray-500 italic">{t('onboarding.pleaseSelectPlanHint', 'Please select a plan from the options above to continue.')}</p>
              )}
            </div>
          </div>
          <Button
            onClick={handleCreateSubscription}
            disabled={!isAuthenticated || !selectedPlan}
            className="w-full text-sm sm:text-base py-2 sm:py-3"
          >
            {!isAuthenticated
              ? t('onboarding.pleaseSignInToContinue')
              : selectedPlan 
                ? t('onboarding.proceedToCheckout')
                : t('onboarding.selectAPlanToContinue', 'Select a Plan to Continue')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default OnboardingPage;

